import React from 'react';
import { App, Button, Modal, Typography } from 'antd';
import { useDispatch } from 'react-redux';

import { prerenderUserPatch } from '../../../actions/prerenderUserActions';
import { useReactivateSubscriptionMutation } from '../api/billingApiLegacySlice';

type SubscriptionReactivationModalProps = {
  open: boolean;
  onCancel: () => void;
  afterReactivate: () => void;
  planName: string;
  discountAvailable?: {
    chargebeeCouponId: string;
    percentage: number;
    months: number;
  };
};

function SubscriptionReactivationModal({
  open,
  onCancel,
  afterReactivate,
  planName,
  discountAvailable,
}: SubscriptionReactivationModalProps) {
  const { message } = App.useApp();
  const [reactivateSubscription, ReactivateSubscriptionResult] = useReactivateSubscriptionMutation();
  const dispatch = useDispatch();

  const onReactivateClick = () => {
    reactivateSubscription({ applyFreeTrialCoupon: !!discountAvailable })
      .unwrap()
      .then(() => {
        afterReactivate();
        dispatch(prerenderUserPatch({ recachingDisabled: false, cancelledAt: null, renderCounter: 0 }));
      })
      .catch((error) => {
        if (error.data?.error?.message) {
          message.error('Cannot reactivate account: ' + error.data.error.message);
        } else {
          message.error('Cannot reactivate account, please try again later.');
        }
      });
  };

  return (
    <Modal
      title={ReactivateSubscriptionResult.isSuccess ? 'Subscription reactivated successfully' : 'Confirm subscription reactivation'}
      open={open}
      onCancel={onCancel}
      closable
      okText="Reactivate subscription"
      footer={
        ReactivateSubscriptionResult.isSuccess
          ? [
              <Button type="primary" onClick={onCancel} key="done">
                Done
              </Button>,
            ]
          : [
              <Button type="default" onClick={onCancel} key="cancel">
                Cancel
              </Button>,
              <Button
                type="primary"
                onClick={onReactivateClick}
                loading={ReactivateSubscriptionResult.isLoading}
                disabled={ReactivateSubscriptionResult.isLoading}
                key="reactivate"
              >
                Reactivate subscription
              </Button>,
            ]
      }
    >
      {!ReactivateSubscriptionResult.isSuccess ? (
        <div>
          <Typography.Paragraph>
            Your {planName} plan is currently cancelled. Reactivating it will restore access immediately and resume
            billing.
          </Typography.Paragraph>
          <Typography.Paragraph>
            You will be charged the prorated amount now, plus past unpaid invoices and overages, if any.
          </Typography.Paragraph>
          <Typography.Paragraph>
            At the end of the billing period, you will be charged the total monthly fee plus overages, if any, until you
            change or cancel your plan.
          </Typography.Paragraph>
          {discountAvailable && (
            <Typography.Paragraph strong>
              You are eligible for a discount of {discountAvailable.percentage}% for {discountAvailable.months} months!
            </Typography.Paragraph>
          )}
        </div>
      ) : (
        <div>
          <Typography.Paragraph>Your account has been reactivated successfully.</Typography.Paragraph>
          <Typography.Paragraph>
            Please keep in mind that some informations related to your account might take a few minutes to update.
          </Typography.Paragraph>
        </div>
      )}
    </Modal>
  );
}

export default SubscriptionReactivationModal;
